import { useState, useRef } from 'react'
import PricingSection from './PricingSection'
import './theme.css'
import './Marketing.css'
import './Pricing.css'

export default function Marketing() {
  const [menuOpen, setMenuOpen] = useState(false)
  const featuresRef = useRef(null)
  const heroRef = useRef(null)
  const pricingRef = useRef(null)
  const testimonialsRef = useRef(null)
  const ctaRef = useRef(null)

  const scrollToSection = ref => {
    ref.current?.scrollIntoView({ behavior: 'smooth' })
  }

  // Handle navigation to app subdomain
  const navigateToApp = (path = '') => {
    // In production, this will navigate to app.echoinbox.ai
    // In development, we'll use a relative path
    const isProduction = process.env.NODE_ENV === 'production'
    const appDomain = isProduction ? 'https://app.echoinbox.ai' : ''
    window.location.href = `${appDomain}${path}`
  }

  return (
    <div className="marketing-container">
      {/* Navigation */}
      <header className="site-header">
        <div className="container">
          <div className="logo">
            <img src="/logo.svg" alt="EchoInbox Logo" className="logo-svg" />
            <span className="logo-text">EchoInbox.ai</span>
          </div>

          <div
            className={`mobile-menu-toggle ${menuOpen ? 'active' : ''}`}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <span></span>
            <span></span>
            <span></span>
          </div>

          <nav className={`main-nav ${menuOpen ? 'open' : ''}`}>
            <ul>
              <li>
                <a
                  href="#features"
                  onClick={e => {
                    e.preventDefault()
                    scrollToSection(featuresRef)
                  }}
                >
                  Features
                </a>
              </li>
              <li>
                <a
                  href="#pricing"
                  onClick={e => {
                    e.preventDefault()
                    scrollToSection(pricingRef)
                  }}
                >
                  Pricing
                </a>
              </li>
              <li className="nav-button">
                <button className="btn btn-login" onClick={() => navigateToApp('/login')}>
                  Login
                </button>
              </li>
              <li className="nav-button">
                <button className="btn btn-signup" onClick={() => navigateToApp('/register')}>
                  Sign Up
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero" ref={heroRef}>
        <div className="hero-background">
          <div className="gradient-overlay"></div>
          <div className="pattern-overlay"></div>
        </div>

        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">
              <span className="text-gradient">AI-Powered</span> <br />
              Email Responses
            </h1>
            <p className="hero-subtitle">
              Save time and respond professionally with AI-generated email replies tailored to your
              style.
            </p>
            <div className="hero-buttons">
              <button className="btn btn-primary" onClick={() => navigateToApp('/register')}>
                Get Started Free
              </button>
              <button className="btn btn-secondary" onClick={() => scrollToSection(featuresRef)}>
                <span className="btn-icon">↓</span> Learn More
              </button>
            </div>
          </div>

          <div className="hero-image">
            <div className="email-card">
              <div className="email-header">
                <div className="email-avatar">JD</div>
                <div className="email-sender">
                  <div className="email-name">John Doe</div>
                  <div className="email-address">john.doe@example.com</div>
                </div>
              </div>
              <div className="email-body">
                <p>Hi Sarah,</p>
                <p>
                  Thank you for reaching out about the project proposal. I've reviewed the documents
                  you sent and I'm impressed with the direction you're taking.
                </p>
                <p>
                  I'd be happy to schedule a call next week to discuss the details further. How does
                  Tuesday at 2pm sound?
                </p>
                <p>Looking forward to our collaboration!</p>
                <p>
                  Best regards,
                  <br />
                  John
                </p>
              </div>
              <div className="email-actions">
                <button className="email-send-btn">Send</button>
              </div>
            </div>
          </div>
        </div>

        <div className="scroll-indicator" onClick={() => scrollToSection(featuresRef)}>
          <div className="mouse">
            <div className="wheel"></div>
          </div>
          <div className="arrow">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features" ref={featuresRef}>
        <div className="container">
          <div className="section-header">
            <div className="section-tag">Features</div>
            <h2>
              Work smarter, <span className="text-gradient">not harder</span>
            </h2>
            <p>
              EchoInbox.ai uses advanced AI to help you manage your inbox efficiently and respond
              professionally.
            </p>
          </div>

          <div className="features-grid">
            {/* Feature 1 */}
            <div className="feature-card">
              <div className="feature-icon">
                <span className="icon-ai">✨</span>
              </div>
              <h3>AI-Powered Responses</h3>
              <p>
                Generate multiple response options tailored to your communication style and the
                context of the email.
              </p>
            </div>

            {/* Feature 2 */}
            <div className="feature-card">
              <div className="feature-icon">
                <span className="icon-gmail">📧</span>
              </div>
              <h3>Gmail Integration</h3>
              <p>
                Seamlessly connect with your Gmail account for real-time email management and
                response generation.
              </p>
            </div>

            {/* Feature 3 */}
            <div className="feature-card">
              <div className="feature-icon">
                <span className="icon-time">⏱️</span>
              </div>
              <h3>Time-Saving Automation</h3>
              <p>
                Save hours each week with automated email response suggestions and smart inbox
                management.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Banner Section */}
      <section className="banner-section">
        <div className="banner-background"></div>
        <div className="banner-pattern"></div>
        <div className="container">
          <div className="banner-content">
            <h2>Reclaim your time and inbox</h2>
            <p>
              The average professional spends 28% of their workday on email. EchoInbox.ai helps you
              cut that time in half.
            </p>
            <button className="btn btn-primary" onClick={() => navigateToApp('/register')}>
              Start Your Free Trial
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <div ref={pricingRef}>
        <PricingSection />
      </div>

      {/* Testimonials Section */}
      <section className="testimonials" ref={testimonialsRef}>
        <div className="container">
          <div className="section-header">
            <div className="section-tag">Testimonials</div>
            <h2>
              Loved by <span className="text-gradient">professionals</span>
            </h2>
            <p>
              Join thousands of professionals who use EchoInbox.ai to manage their inbox
              efficiently.
            </p>
          </div>

          <div className="testimonials-grid">
            {/* Testimonial 1 */}
            <div className="testimonial-card">
              <div className="testimonial-content">
                <p>
                  "EchoInbox has transformed how I handle emails. The AI suggestions are spot-on and
                  save me hours every week. It's like having a personal assistant for my inbox."
                </p>
              </div>
              <div className="testimonial-author">
                <div className="testimonial-avatar">SJ</div>
                <div className="testimonial-info">
                  <div className="testimonial-name">Sarah Johnson</div>
                  <div className="testimonial-title">Marketing Director</div>
                </div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="testimonial-card">
              <div className="testimonial-content">
                <p>
                  "As a founder, I'm constantly overwhelmed with emails. EchoInbox helps me stay
                  responsive without sacrificing my focus time. It's been a game-changer for my
                  productivity."
                </p>
              </div>
              <div className="testimonial-author">
                <div className="testimonial-avatar">MC</div>
                <div className="testimonial-info">
                  <div className="testimonial-name">Michael Chen</div>
                  <div className="testimonial-title">Startup Founder</div>
                </div>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="testimonial-card">
              <div className="testimonial-content">
                <p>
                  "We've implemented EchoInbox across our entire organization. The ROI in terms of
                  time saved is incredible. Our team can focus on high-value work instead of
                  crafting email responses."
                </p>
              </div>
              <div className="testimonial-author">
                <div className="testimonial-avatar">TA</div>
                <div className="testimonial-info">
                  <div className="testimonial-name">Techstars Accelerator</div>
                  <div className="testimonial-title">Company</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta" ref={ctaRef}>
        <div className="container">
          <div className="section-tag">Ready?</div>
          <h2>Start saving time today</h2>
          <p>
            Join thousands of professionals who use EchoInbox.ai to manage their inbox efficiently.
          </p>
          <button className="btn btn-primary btn-large" onClick={() => navigateToApp('/register')}>
            <span className="btn-icon">✨</span> Get Started Free
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="site-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-logo">
              <img src="/logo.svg" alt="EchoInbox Logo" className="logo-svg" />
              <div className="footer-tagline">Made with ♥ in London</div>
            </div>
            <div className="footer-copyright">
              © {new Date().getFullYear()} EchoInbox.ai. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
